import type {
  Basket,
  Data,
  Message,
  Package, PackageType,
} from './types'

export const LocalStorageBasketKey = 'tebex-basket'
export const StateBasketKey = 'tebex-basket'

export const UrlQueryKey = 'tebex'
export enum UrlQueryValue {
  Completed = 'completed',
  Canceled = 'cancel',
}

export function useTebex() {
  const config = useRuntimeConfig()
  const webstoreId = config.public.tebexWebstoreId
  const shopUrl = config.public.shopUrl

  // global state (request scoped)
  const basket = useState<Basket | null>(StateBasketKey, () => null)
  const basketSize = computed(() =>
    basket.value
      ?.packages
      ?.map(item => item.in_basket.quantity)
      ?.reduce((c, a) => c + a, 0)
      ?? 0,
  )

  // sync basket with client localstorage
  watch(basket, (value, _) => {
    if (import.meta.server) {
      console.warn('cannot save tebex basket on server')
      return
    }
    else if (value?.ident) {
      localStorage.setItem(LocalStorageBasketKey, value?.ident)
    }
    else {
      localStorage.removeItem(LocalStorageBasketKey)
    }
  })

  // base fetcher for tebex
  const tebex = $fetch.create({
    baseURL: 'https://headless.tebex.io',
  })

  // basket
  const createBasket = async (username: string) => {
    return tebex<Data<Basket>>(
      `/api/accounts/${webstoreId}/baskets`,
      { method: 'POST', body: {
        username: username,
        // urls, might be unnecessary
        complete_url: shopUrl + `?${UrlQueryKey}=${UrlQueryValue.Completed}`,
        cancel_url: shopUrl + `?${UrlQueryKey}=${UrlQueryValue.Canceled}`,
        complete_auto_redirect: false,
      } },
    )
      .then(fetched => fetched.data)
  }
  const createGlobalBasket = async (username: string) => {
    return createBasket(username)
      .then((fetched) => {
        basket.value = fetched
      })
  }
  const loadBasket = async () => {
    const basketIdent = localStorage.getItem(LocalStorageBasketKey)
    if (basketIdent) {
      await tebex<Data<Basket>>(`/api/accounts/${webstoreId}/baskets/${basketIdent}`)
        .then((fetched) => {
          if (!fetched.data.complete) {
            basket.value = fetched.data
          }
          else {
            localStorage.removeItem(LocalStorageBasketKey)
          }
        })
        .catch((e) => {
          // forget baskets that tebex no longer knows, instead of requesting them again on every visit
          if (e?.statusCode === 404) {
            localStorage.removeItem(LocalStorageBasketKey)
          }
        })
    }
  }
  // the checkout can change the basket on its own (upsells, coupons, creator codes) or complete it
  const refreshBasket = async () => {
    if (!basket.value) {
      return
    }
    const fetched = await tebex<Data<Basket>>(`/api/accounts/${webstoreId}/baskets/${basket.value.ident}`)
    basket.value = fetched.data.complete ? null : fetched.data
  }
  const abandonBasket = async () => {
    basket.value = null
    // the watcher misses this if no basket was loaded yet (e.g. when returning from the hosted checkout)
    localStorage.removeItem(LocalStorageBasketKey)
  }

  // basket packages
  const addPackage = async (packageId: number, type: PackageType = 'single', quantity: number = 1, variableData: object = {}) => {
    return tebex<Data<Basket>>(
      `/api/baskets/${basket.value?.ident}/packages`,
      { method: 'POST', body: {
        package_id: packageId,
        quantity: quantity,
        type: type,
        variable_data: variableData,
      } },
    )
      .then((fetched) => {
        basket.value = fetched.data
      })
  }
  const removePackage = async (packageId: number) => {
    return tebex<Data<Basket>>(
      `/api/baskets/${basket.value?.ident}/packages/remove`,
      { method: 'POST', body: { package_id: packageId } },
    )
      .then((fetched) => {
        basket.value = fetched.data
      })
  }
  const updatePackage = async (packageId: number, quantity: number) => {
    return tebex<Data<Basket>>(
      `/api/baskets/${basket.value?.ident}/packages/${packageId}`,
      { method: 'PUT', body: { quantity: quantity } },
    )
      .then((fetched) => {
        basket.value = fetched.data
      })
  }

  // basket creator codes
  const applyCreatorCode = async (code: string) => {
    return tebex<Message>(
      `/api/accounts/${webstoreId}/baskets/${basket.value?.ident}/creator-codes`,
      { method: 'POST', body: { creator_code: code } },
    ).then(() => {})
  }
  const removeCreatorCode = async () => {
    return tebex<Message>(
      `/api/accounts/${webstoreId}/baskets/${basket.value?.ident}/creator-codes/remove`,
      { method: 'POST' },
    ).then(() => {})
  }

  // basket gift cards
  const applyGiftCard = async (code: string) => {
    return tebex<Message>(
      `/api/accounts/${webstoreId}/baskets/${basket.value?.ident}/giftcards`,
      { method: 'POST', body: { card_number: code } },
    ).then(() => {})
  }
  const removeGiftCard = async (code: string) => {
    return tebex<Message>(
      `/api/accounts/${webstoreId}/baskets/${basket.value?.ident}/giftcards/remove`,
      { method: 'POST', body: { card_number: code } },
    ).then(() => {})
  }

  // basket coupons
  const applyCoupon = async (code: string) => {
    return tebex<Message>(
      `/api/accounts/${webstoreId}/baskets/${basket.value?.ident}/coupons`,
      { method: 'POST', body: { coupon_code: code } },
    ).then(() => {})
  }
  const removeCoupon = async (code: string) => {
    return tebex<Message>(
      `/api/accounts/${webstoreId}/baskets/${basket.value?.ident}/coupons/remove`,
      { method: 'POST', body: { coupon_code: code } },
    ).then(() => {})
  }

  // packages
  const getPackages = async (ident: string | null) => {
    return tebex<Data<Package[]>>(
      `/api/accounts/${webstoreId}/packages`,
      { method: 'GET', query: ident ? { basketIdent: ident } : {} },
    )
      .then((data) => {
        return Object.fromEntries(data.data.map(item => ([item.id, item])))
      })
  }

  return {
    tebex,
    getPackages,
    createBasket,
    createGlobalBasket,
    abandonBasket,
    loadBasket,
    refreshBasket,
    addPackage,
    removePackage,
    updatePackage,
    applyCreatorCode,
    removeCreatorCode,
    applyGiftCard,
    removeGiftCard,
    applyCoupon,
    removeCoupon,
    basketSize,
    // readonly basket
    basket: computed(() => basket.value),
  }
}
